package org.teddapp.project.ui.vault

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddConfirmDialog
import org.teddapp.project.ui.TeddEmptyState
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddFilterRow
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddSnackbarHost
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.adaptive.WithWindowClass
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.nav.TeddBottomNav
import org.teddapp.project.ui.nav.TeddNavRail
import org.teddapp.project.ui.settings.SettingsDrawerHost
import org.teddapp.project.ui.settings.rememberSettingsDrawer
import org.teddapp.project.ui.teddCardShadow
import org.teddapp.project.ui.teddClickable
import org.teddapp.project.ui.teddPasswordTransformation
import org.teddapp.project.vault.VaultCipher
import org.teddapp.project.vault.VaultFormat
import org.teddapp.project.ui.TeddTab
import org.teddapp.project.ui.TeddTabs
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTagTone
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.foundation.layout.size

/**
 * Trousseau : identifiants, mots de passe, et blocs à recopier (SQL, JSON, commandes…), chiffrés
 * sur l'appareil. Une feuille de triche qu'on rouvre pour copier-coller.
 *
 * Un trousseau s'ouvre avec sa phrase secrète, qui n'est écrite nulle part. Ouvert, il
 * montre ses entrées ; on le reverrouille d'un geste, et quitter l'écran le referme.
 *
 * @param previewPassphrase pour le banc de captures : ouvre le premier trousseau à l'arrivée.
 */
@Composable
fun VaultScreen(
    repos: TeddRepositories,
    onNavigate: (Section) -> Unit = {},
    modifier: Modifier = Modifier,
    previewPassphrase: String? = null,
) {
    val model = rememberVaultModel(repos)
    val ready = model.selectedKeychain != null
    LaunchedEffect(ready) { if (ready && previewPassphrase != null && !model.unlocked) model.unlock(previewPassphrase) }

    WithWindowClass(modifier.fillMaxSize()) { wc ->
        Box(modifier = Modifier.fillMaxSize()) {
            if (wc == WindowClass.Compact) VaultCompact(model, onNavigate) else VaultExpanded(model, onNavigate)
            TeddSnackbarHost(model.snackbar, modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = if (wc == WindowClass.Compact) 96.dp else 28.dp))
        }
    }
    model.confirmDeleteItem?.let { e ->
        TeddConfirmDialog(
            title = "Supprimer cette entrée ?",
            message = "« ${e.entry.title.ifBlank { "Sans titre" }} » et son secret seront supprimés.",
            confirmLabel = "Supprimer l'entrée",
            onConfirm = { model.deleteItem(e) },
            onDismiss = { model.confirmDeleteItem = null },
        )
    }
    model.confirmDeleteKeychain?.let { kc ->
        TeddConfirmDialog(
            title = "Supprimer le trousseau ?",
            message = "« ${kc.name} » et toutes ses entrées seront supprimés.",
            confirmLabel = "Supprimer le trousseau",
            onConfirm = { model.deleteKeychain(kc) },
            onDismiss = { model.confirmDeleteKeychain = null },
        )
    }
}

// ── Grand écran ────────────────────────────────────────────────────────

/** Les trousseaux : 232 dp par défaut, de 180 à 360. */
private val KEYCHAINS_PANE = org.teddapp.project.ui.PaneSpec("vault.keychains", default = 232.dp, min = 180.dp, max = 360.dp, collapseBelow = 140.dp)

/** Les entrées d'un trousseau : 320 dp par défaut, de 240 à 520. */
private val ENTRIES_PANE = org.teddapp.project.ui.PaneSpec("vault.entries", default = 320.dp, min = 240.dp, max = 520.dp)

@Composable
private fun VaultExpanded(model: VaultModel, onNavigate: (Section) -> Unit) {
    val drawer = rememberSettingsDrawer()
    SettingsDrawerHost(drawer, onNavigate = onNavigate) {
        Row(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
            TeddNavRail(active = Section.Vault, onNavigate = onNavigate, onSettingsClick = drawer::open)
            when {
                !VaultCipher.isAvailable -> Centered { Unavailable() }
                !model.loaded -> Unit
                model.keychains.isEmpty() -> Centered { CreatePane(model, first = true) }
                else -> {
                    // Les filets entre les colonnes sont les poignées : on y règle leur largeur.
                    org.teddapp.project.ui.TeddSidePane(
                        spec = KEYCHAINS_PANE,
                        title = "les trousseaux",
                        collapsedContent = { expand ->
                            org.teddapp.project.ui.TeddPaneRail(
                                expand = expand,
                                title = "les trousseaux",
                                items = model.keychains.map { kc ->
                                    org.teddapp.project.ui.PaneRailItem(
                                        kc.id, kc.name, if (model.isUnlocked(kc.id)) "check" else "lock",
                                        selected = !model.creating && kc.id == model.selectedKeychain?.id,
                                    ) { model.select(kc.id) }
                                },
                            )
                        },
                    ) {
                        Column(
                            modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
                            verticalArrangement = Arrangement.spacedBy(2.dp),
                        ) { KeychainsColumn(model) }
                    }
                    when {
                        model.creating -> Centered { CreatePane(model, first = false) }
                        !model.unlocked -> Centered { UnlockPane(model) }
                        else -> {
                            val entries = model.openEntries()
                            org.teddapp.project.ui.TeddSidePane(spec = ENTRIES_PANE, title = "les entrées", icon = "list") {
                                Column(
                                    modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
                                    verticalArrangement = Arrangement.spacedBy(TeddSpace.S1),
                                ) { EntriesColumn(model, entries, compact = false) }
                            }
                            Box(
                                modifier = Modifier.weight(1f).fillMaxHeight().verticalScroll(rememberScrollState())
                                    .padding(start = TeddSpace.S8, end = TeddSpace.S8, top = TeddSpace.S6, bottom = TeddSpace.S12),
                            ) {
                                Box(modifier = Modifier.widthIn(max = 560.dp)) {
                                    val draft = model.draft
                                    val selected = entries.firstOrNull { it.row.id == model.selectedItemId } ?: entries.firstOrNull()
                                    when {
                                        draft != null -> EntryEditor(model, draft)
                                        selected != null -> EntryDetail(model, selected)
                                        else -> TeddEmptyState(
                                            title = "Un trousseau vide",
                                            message = "Colle une requête SQL, un JSON, une commande, ou note un identifiant et son mot de passe. Chaque entrée est chiffrée avant d'être écrite.",
                                            icon = "lock",
                                            actionLabel = "Nouvelle entrée",
                                            onAction = { model.newEntry() },
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun Rule() {
    Box(Modifier.width(1.dp).fillMaxHeight().background(TeddTokens.Line))
}

@Composable
private fun androidx.compose.foundation.layout.RowScope.Centered(content: @Composable () -> Unit) {
    Box(modifier = Modifier.weight(1f).fillMaxHeight().padding(TeddSpace.S8), contentAlignment = Alignment.Center) {
        Box(modifier = Modifier.widthIn(max = 440.dp)) { content() }
    }
}

@Composable
private fun Unavailable() {
    TeddEmptyState(
        title = "Trousseau indisponible ici",
        message = "Le chiffrement n'est pas encore en place sur cette plateforme. Rien n'y est stocké en attendant.",
        icon = "lock",
    )
}

// ── Trousseaux ─────────────────────────────────────────────────────────

@Composable
private fun KeychainsColumn(model: VaultModel) {
    Text("TROUSSEAUX", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(start = 10.dp, bottom = 6.dp))
    val current = model.selectedKeychain?.id
    model.keychains.forEach { kc ->
        TeddFilterRow(
            label = kc.name,
            selected = !model.creating && kc.id == current,
            onClick = { model.select(kc.id) },
            leading = {
                // Cadenas fermé : verrouillé. Coche : ouvert, tant qu'on reste sur l'écran.
                TeddIcon(
                    name = if (model.isUnlocked(kc.id)) "check" else "lock",
                    size = 16.dp,
                    color = if (model.isUnlocked(kc.id)) TeddTokens.Success else TeddTokens.InkMuted,
                )
            },
        )
    }
    TeddButton(
        text = "Nouveau trousseau",
        onClick = { model.creating = true; model.error = null },
        kind = TeddButtonKind.Ghost,
        size = TeddButtonSize.Sm,
        modifier = Modifier.padding(top = 6.dp),
        icon = "plus",
    )
}

/** Création : un nom, une phrase secrète saisie deux fois, et ce qu'il faut savoir avant. */
@Composable
private fun CreatePane(model: VaultModel, first: Boolean) {
    var name by remember { mutableStateOf(if (first) "Personnel" else "") }
    var passphrase by remember { mutableStateOf("") }
    var confirm by remember { mutableStateOf("") }
    var reveal by remember { mutableStateOf(false) }
    val transform = if (reveal) VisualTransformation.None else teddPasswordTransformation()
    val mismatch = confirm.isNotEmpty() && confirm != passphrase
    TeddCard(title = if (first) "Crée ton trousseau" else "Nouveau trousseau", icon = "lock") {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
            Text(
                "Tes identifiants, tes mots de passe et tes blocs à recopier, chiffrés sur l'appareil, puis gardés sur le serveur pour les retrouver partout.",
                color = TeddTokens.InkMuted, style = TeddType.Body,
            )
            TeddField(label = "Nom", value = name, onValueChange = { name = it }, placeholder = "Personnel, Travail…")
            TeddField(
                label = "Phrase secrète (facultative)",
                value = passphrase,
                onValueChange = { passphrase = it },
                visualTransformation = transform,
                placeholder = "Vide : s'ouvre tout seul, mais sur cet appareil seulement",
                trailingContent = { RevealButton(reveal) { reveal = !reveal } },
            )
            if (passphrase.isNotEmpty()) {
                TeddField(label = "Confirme la phrase secrète", value = confirm, onValueChange = { confirm = it }, visualTransformation = transform)
            }
            if (mismatch) Text("Les deux phrases ne correspondent pas.", color = TeddTokens.Danger, style = TeddType.Caption)
            else if (passphrase.isNotEmpty() && passphrase.length < 12) {
                Text("12 caractères au moins. Plusieurs mots sans rapport font une bonne phrase.", color = TeddTokens.InkMuted, style = TeddType.Caption)
            }
            if (passphrase.isEmpty()) {
                Notice("Sans phrase, le trousseau s'ouvre tout seul ici, mais il restera fermé sur tes autres appareils : sa clé est gardée sur cet appareil seulement. Avec une phrase, tu l'ouvres partout.")
            } else {
                Notice("La phrase secrète n'est enregistrée nulle part. Si tu la perds, personne ne peut rouvrir ce trousseau, pas même toi.")
            }
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TeddButton(
                    text = if (model.busy) "Création…" else "Créer le trousseau",
                    onClick = { model.create(name, passphrase) },
                    kind = TeddButtonKind.Primary,
                    enabled = !model.busy && (passphrase.isEmpty() || (passphrase.length >= 12 && confirm == passphrase)),
                )
                if (!first) TeddButton(text = "Annuler", onClick = { model.creating = false }, kind = TeddButtonKind.Ghost)
            }
        }
    }
}

@Composable
private fun UnlockPane(model: VaultModel) {
    val kc = model.selectedKeychain ?: return
    var passphrase by remember(kc.id) { mutableStateOf("") }
    var reveal by remember(kc.id) { mutableStateOf(false) }
    // Un trousseau sans phrase s'ouvre de lui-même, avec le secret de l'appareil.
    val auto = model.selectedIsAuto
    LaunchedEffect(kc.id, auto) { if (auto) model.unlock("") }
    if (auto && model.error == null) {
        TeddCard(title = kc.name, icon = "lock") {
            Text("Ouverture…", color = TeddTokens.InkMuted, style = TeddType.Body)
        }
        return
    }
    TeddCard(title = kc.name, icon = "lock") {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
            if (!auto) {
                Text("Ce trousseau est verrouillé. Sa phrase secrète l'ouvre jusqu'à ce que tu quittes l'écran.", color = TeddTokens.InkMuted, style = TeddType.Body)
                TeddField(
                    label = "Phrase secrète",
                    value = passphrase,
                    onValueChange = { passphrase = it; model.error = null },
                    visualTransformation = if (reveal) VisualTransformation.None else teddPasswordTransformation(),
                    trailingContent = { RevealButton(reveal) { reveal = !reveal } },
                )
            }
            model.error?.let { message ->
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    TeddIcon(name = "alert", size = 16.dp, color = TeddTokens.Danger)
                    Text(message, color = TeddTokens.Danger, style = TeddType.Caption)
                }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                if (!auto) {
                    TeddButton(
                        text = if (model.busy) "Ouverture…" else "Déverrouiller",
                        onClick = { model.unlock(passphrase) },
                        kind = TeddButtonKind.Primary,
                        enabled = !model.busy && passphrase.isNotEmpty(),
                    )
                }
                Spacer(Modifier.weight(1f))
                TeddButton(text = "Supprimer", onClick = { model.confirmDeleteKeychain = kc }, kind = TeddButtonKind.Ghost, icon = "trash")
            }
        }
    }
}

/**
 * Un trousseau sans phrase ne s'ouvre que sur l'appareil qui l'a créé. Le protéger par
 * une phrase le rend accessible partout : on le propose, on ne l'impose pas.
 */
@Composable
private fun ProtectPane(model: VaultModel) {
    var open by remember { mutableStateOf(false) }
    var passphrase by remember { mutableStateOf("") }
    var confirm by remember { mutableStateOf("") }
    Column(
        modifier = Modifier.fillMaxWidth().padding(top = TeddSpace.S2),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S2),
    ) {
        Notice("Sans phrase, ce trousseau ne s'ouvre que sur cet appareil. Protège-le par une phrase pour l'ouvrir partout.")
        if (!open) {
            TeddButton(text = "Protéger par une phrase", onClick = { open = true }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "lock")
        } else {
            TeddField(label = "Phrase secrète", value = passphrase, onValueChange = { passphrase = it }, visualTransformation = teddPasswordTransformation())
            TeddField(label = "Confirme la phrase secrète", value = confirm, onValueChange = { confirm = it }, visualTransformation = teddPasswordTransformation())
            val ok = passphrase.length >= 12 && confirm == passphrase
            Text(
                if (confirm.isNotEmpty() && confirm != passphrase) "Les deux phrases ne correspondent pas."
                else "12 caractères au moins. Perdue, elle ne se retrouve pas.",
                color = if (confirm.isNotEmpty() && confirm != passphrase) TeddTokens.Danger else TeddTokens.InkMuted,
                style = TeddType.Caption,
            )
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TeddButton(
                    text = if (model.busy) "Protection…" else "Protéger",
                    onClick = { model.protect(passphrase); open = false },
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Sm,
                    enabled = ok && !model.busy,
                )
                TeddButton(text = "Annuler", onClick = { open = false }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
            }
        }
    }
}

@Composable
private fun Notice(text: String) {
    Row(
        modifier = Modifier.fillMaxWidth().background(TeddTokens.WarningSoft, RoundedCornerShape(TeddTokens.RSm)).padding(TeddSpace.S3),
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
    ) {
        TeddIcon(name = "alert", size = 16.dp, color = TeddTokens.Warning)
        Text(text, color = TeddTokens.Ink, style = TeddType.Caption, modifier = Modifier.weight(1f))
    }
}

@Composable
private fun RevealButton(shown: Boolean, onToggle: () -> Unit) {
    TeddIconButton(onClick = onToggle, size = 28.dp) {
        TeddIcon(name = if (shown) "eye-off" else "eye", size = 16.dp, color = TeddTokens.InkMuted)
    }
}

// ── Entrées ────────────────────────────────────────────────────────────

@Composable
private fun EntriesColumn(model: VaultModel, entries: List<OpenEntry>, compact: Boolean) {
    val kc = model.selectedKeychain ?: return
    Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
        Column(modifier = Modifier.weight(1f).padding(start = if (compact) 0.dp else TeddSpace.S2)) {
            Text(kc.name, style = TeddType.BodyStrong, color = TeddTokens.Ink, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text(
                text = "${entries.size} entrée${if (entries.size > 1) "s" else ""} · ouvert",
                style = TeddType.MonoSm.copy(fontSize = 11.sp),
                color = TeddTokens.InkMuted,
            )
        }
        // Verrouiller : le cadenas, à portée de main à côté du nom.
        TeddIconButton(onClick = { model.lock() }) { TeddIcon(name = "lock", size = 16.dp, color = TeddTokens.InkMuted) }
        TeddButton(text = "Entrée", onClick = { model.newEntry() }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "plus")
    }
    // Le geste de la feuille de triche : ce qui est dans le presse-papiers devient une entrée.
    val clipboard = LocalClipboardManager.current
    TeddButton(
        text = "Coller une entrée",
        onClick = {
            val pasted = clipboard.getText()?.text.orEmpty()
            if (pasted.isBlank()) model.snackbar.show("Le presse-papiers est vide") else model.newEntryFrom(pasted)
        },
        kind = TeddButtonKind.Ghost,
        size = TeddButtonSize.Sm,
        icon = "cards",
        modifier = Modifier.padding(top = TeddSpace.S1),
    )
    if (model.selectedIsAuto) ProtectPane(model)
    if (entries.size > 4) {
        Spacer(Modifier.height(TeddSpace.S2))
        TeddField(label = "Recherche", value = model.search, onValueChange = { model.search = it }, placeholder = "Titre, identifiant, contenu…")
    }
    Spacer(Modifier.height(TeddSpace.S2))
    val q = model.search.trim()
    val visible = if (q.isEmpty()) entries else entries.filter {
        it.entry.title.contains(q, ignoreCase = true) || it.entry.username.contains(q, ignoreCase = true) ||
            it.entry.url.contains(q, ignoreCase = true) || it.entry.content.contains(q, ignoreCase = true)
    }
    if (entries.isNotEmpty() && visible.isEmpty()) {
        Text("Aucune entrée ne correspond.", color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.padding(TeddSpace.S2))
    }
    val current = model.selectedItemId ?: entries.firstOrNull()?.row?.id
    visible.forEach { e ->
        EntryRow(e, selected = !compact && model.draft == null && e.row.id == current) {
            model.selectedItemId = e.row.id
            model.draft = null
        }
    }
}

/** Une ligne de la liste : le titre et l'identifiant. Le secret n'y paraît jamais. */
@Composable
private fun EntryRow(e: OpenEntry, selected: Boolean, onClick: () -> Unit) {
    val shape = RoundedCornerShape(TeddTokens.RMd)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .then(if (selected) Modifier.teddCardShadow(shape) else Modifier)
            .teddClickable(
                onClick = onClick,
                shape = shape,
                fill = if (selected) TeddTokens.Surface else Color.Transparent,
                hover = if (selected) TeddTokens.Surface else TeddTokens.SurfaceSunken,
                role = null,
            )
            .padding(horizontal = TeddSpace.S3, vertical = 10.dp),
        verticalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Text(
                e.entry.title.ifBlank { "Sans titre" },
                color = TeddTokens.Ink, style = TeddType.BodyStrong, maxLines = 1, overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f),
            )
            if (e.entry.content.isNotEmpty()) TeddTag(VaultFormat.of(e.entry.format).label, tone = TeddTagTone.Outline)
        }
        val sub = e.entry.username.ifBlank { e.entry.url }.ifBlank { e.entry.content.lineSequence().firstOrNull { it.isNotBlank() }?.trim().orEmpty() }
        if (sub.isNotBlank()) Text(sub, color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 1, overflow = TextOverflow.Ellipsis)
    }
}

@Composable
private fun EntryDetail(model: VaultModel, e: OpenEntry, onBack: (() -> Unit)? = null) {
    val clipboard = LocalClipboardManager.current
    val uri = LocalUriHandler.current
    var reveal by remember(e.row.id) { mutableStateOf(false) }
    val copy: (String, String) -> Unit = { what, value ->
        clipboard.setText(AnnotatedString(value))
        model.snackbar.show("$what copié")
    }
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S6), modifier = Modifier.fillMaxWidth()) {
        if (onBack != null) TeddButton(text = "Entrées", onClick = onBack, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "chevron-left")
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            org.teddapp.project.ui.TeddFitText(e.entry.title.ifBlank { "Sans titre" }, style = TeddType.Title1, minFontSize = 18.sp, maxLines = 2, modifier = Modifier.weight(1f))
            TeddButton(text = "Modifier", onClick = { model.edit(e) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "edit")
            TeddIconButton(onClick = { model.confirmDeleteItem = e }) {
                TeddIcon(name = "trash", size = 16.dp, color = TeddTokens.InkMuted)
            }
        }
        if (e.entry.content.isNotEmpty()) {
            ContentBlock(e.entry.content, VaultFormat.of(e.entry.format)) { copy("Contenu", e.entry.content) }
        }
        val hasCredentials = e.entry.username.isNotBlank() || e.entry.secret.isNotEmpty() || e.entry.url.isNotBlank() || e.entry.content.isEmpty()
        if (hasCredentials) TeddCard(padding = androidx.compose.foundation.layout.PaddingValues(horizontal = TeddSpace.S5, vertical = TeddSpace.S2)) {
            if (e.entry.username.isNotBlank()) {
                DetailLine("Identifiant", e.entry.username, mono = true) {
                    TeddButton(text = "Copier", onClick = { copy("Identifiant", e.entry.username) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
                }
                Divider()
            }
            DetailLine("Secret", if (reveal) e.entry.secret.ifEmpty { "(vide)" } else "••••••••••••", mono = true) {
                RevealButton(reveal) { reveal = !reveal }
                TeddButton(text = "Copier", onClick = { copy("Secret", e.entry.secret) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, enabled = e.entry.secret.isNotEmpty())
            }
            if (e.entry.url.isNotBlank()) {
                Divider()
                DetailLine("Adresse", e.entry.url, mono = true) {
                    TeddButton(text = "Ouvrir", onClick = { runCatching { uri.openUri(e.entry.url) } }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "arrow-right")
                }
            }
        }
        if (e.entry.notes.isNotBlank()) {
            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                Text("NOTES", style = TeddType.Overline, color = TeddTokens.InkMuted)
                Text(e.entry.notes, style = TeddType.Body, color = TeddTokens.Ink)
            }
        }
    }
}

/** Le bloc à recopier : tel qu'il a été collé, en chasse fixe, avec « Copier » à portée de main. */
@Composable
private fun ContentBlock(content: String, format: VaultFormat, onCopy: () -> Unit) {
    val shape = RoundedCornerShape(TeddTokens.RMd)
    Column(modifier = Modifier.fillMaxWidth().background(TeddTokens.SurfaceSunken, shape)) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(start = TeddSpace.S4, end = TeddSpace.S2, top = TeddSpace.S2, bottom = TeddSpace.S2),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
        ) {
            TeddTag(format.label, tone = TeddTagTone.Outline)
            val lines = content.lines().size
            Text("$lines ligne${if (lines > 1) "s" else ""}", color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), modifier = Modifier.weight(1f))
            TeddButton(text = "Copier", onClick = onCopy, kind = TeddButtonKind.Primary, size = TeddButtonSize.Sm)
        }
        Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
        // Les lignes ne sont pas repliées : une requête se lit sur sa largeur, on fait défiler.
        SelectionContainer {
            Text(
                text = content,
                color = TeddTokens.Ink,
                style = TeddType.MonoSm.copy(fontSize = 13.sp, lineHeight = 20.sp),
                softWrap = false,
                modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()).padding(TeddSpace.S4),
            )
        }
    }
}

/** Zone de saisie du bloc : plusieurs lignes, chasse fixe, rien n'est reformaté. */
@Composable
private fun ContentArea(value: String, onValueChange: (String) -> Unit) {
    val interaction = remember { MutableInteractionSource() }
    val focused by interaction.collectIsFocusedAsState()
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = 168.dp, max = 360.dp)
            .background(TeddTokens.Surface, shape)
            .border(1.dp, if (focused) TeddTokens.Ink else TeddTokens.LineStrong, shape)
            .verticalScroll(rememberScrollState())
            .padding(TeddSpace.S3),
    ) {
        if (value.isEmpty()) {
            Text("Colle ici une requête, un JSON, une commande…", color = TeddTokens.InkSubtle, style = TeddType.MonoSm.copy(fontSize = 13.sp, lineHeight = 20.sp))
        }
        BasicTextField(
            value = value,
            onValueChange = onValueChange,
            modifier = Modifier.fillMaxWidth(),
            interactionSource = interaction,
            textStyle = TeddType.MonoSm.copy(fontSize = 13.sp, lineHeight = 20.sp, color = TeddTokens.Ink),
            cursorBrush = SolidColor(TeddTokens.Ink),
        )
    }
}

@Composable
private fun Divider() {
    Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
}

@Composable
private fun DetailLine(label: String, value: String, mono: Boolean, actions: @Composable () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = TeddSpace.S3),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
    ) {
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(label.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted)
            Text(value, style = if (mono) TeddType.MonoSm.copy(fontSize = 14.sp) else TeddType.Body, color = TeddTokens.Ink, maxLines = 2, overflow = TextOverflow.Ellipsis)
        }
        actions()
    }
}

@Composable
private fun EntryEditor(model: VaultModel, draft: EntryDraft) {
    var reveal by remember(draft) { mutableStateOf(draft.existing == null) }
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4), modifier = Modifier.fillMaxWidth()) {
        Text(if (draft.existing == null) "Nouvelle entrée" else "Modifier l'entrée", style = TeddType.Title1, color = TeddTokens.Ink)
        TeddField(label = "Titre", value = draft.title, onValueChange = { draft.title = it }, placeholder = "Requête des comptes récents, serveur de production…")
        val clipboard = LocalClipboardManager.current
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                Text("Contenu à recopier", style = TeddType.Label, color = TeddTokens.Ink, modifier = Modifier.weight(1f))
                TeddButton(
                    text = "Coller",
                    onClick = { clipboard.getText()?.text?.takeIf { it.isNotBlank() }?.let { draft.paste(it) } },
                    kind = TeddButtonKind.Secondary,
                    size = TeddButtonSize.Sm,
                    icon = "cards",
                )
            }
            TeddTabs(
                tabs = VaultFormat.entries.map { TeddTab(it.key, it.label) },
                selected = draft.format.key,
                onSelect = { draft.format = VaultFormat.of(it) },
            )
            ContentArea(draft.content) { draft.content = it }
        }
        Text("IDENTIFIANTS, SI BESOIN", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(top = TeddSpace.S2))
        TeddField(label = "Identifiant", value = draft.username, onValueChange = { draft.username = it }, placeholder = "Nom d'utilisateur ou e-mail")
        Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            TeddField(
                label = "Secret",
                value = draft.secret,
                onValueChange = { draft.secret = it },
                modifier = Modifier.weight(1f),
                visualTransformation = if (reveal) VisualTransformation.None else teddPasswordTransformation(),
                trailingContent = { RevealButton(reveal) { reveal = !reveal } },
            )
            TeddButton(
                text = "Générer",
                onClick = { draft.secret = model.generateSecret(); reveal = true },
                kind = TeddButtonKind.Secondary,
                icon = "repeat",
                modifier = Modifier.padding(bottom = 3.dp),
            )
        }
        TeddField(label = "Adresse", value = draft.url, onValueChange = { draft.url = it }, placeholder = "https://…")
        TeddField(label = "Notes", value = draft.notes, onValueChange = { draft.notes = it }, placeholder = "Question secrète, code de secours…")
        Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2), modifier = Modifier.padding(top = TeddSpace.S2)) {
            TeddButton(
                text = "Enregistrer",
                onClick = { model.saveDraft() },
                kind = TeddButtonKind.Primary,
                enabled = draft.title.isNotBlank() || draft.secret.isNotEmpty() || draft.content.isNotBlank(),
            )
            TeddButton(text = "Annuler", onClick = { model.draft = null }, kind = TeddButtonKind.Ghost)
        }
    }
}

// ── Téléphone ──────────────────────────────────────────────────────────

@Composable
private fun VaultCompact(model: VaultModel, onNavigate: (Section) -> Unit) {
    val clipboard = LocalClipboardManager.current
    Column(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg).statusBarsPadding()) {
        Column(
            modifier = Modifier.weight(1f).fillMaxWidth().verticalScroll(rememberScrollState()).padding(TeddSpace.S4),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S3),
        ) {
            val entries = if (model.unlocked) model.openEntries() else emptyList()
            val draft = model.draft
            val selected = entries.firstOrNull { it.row.id == model.selectedItemId }
            when {
                !VaultCipher.isAvailable -> { VaultTitle(null); Unavailable() }
                !model.loaded -> VaultTitle(null)
                model.keychains.isEmpty() -> { VaultTitle(null); CreatePane(model, first = true) }
                model.creating -> { VaultTitle(null); CreatePane(model, first = false) }
                !model.unlocked -> {
                    VaultTitle(null)
                    KeychainChips(model)
                    UnlockPane(model)
                }
                draft != null -> EntryEditor(model, draft)
                selected != null -> EntryDetail(model, selected, onBack = { model.selectedItemId = null })
                else -> {
                    VaultTitle(onLock = { model.lock() })
                    KeychainChips(model)
                    // Les deux gestes de la feuille de triche, côte à côte : coller ce qu'on vient de copier, ou écrire.
                    Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                        TeddButton(
                            text = "Coller",
                            onClick = {
                                val pasted = clipboard.getText()?.text.orEmpty()
                                if (pasted.isBlank()) model.snackbar.show("Le presse-papiers est vide") else model.newEntryFrom(pasted)
                            },
                            kind = TeddButtonKind.Primary,
                            icon = "cards",
                            modifier = Modifier.weight(1f),
                        )
                        TeddButton(text = "Nouvelle entrée", onClick = { model.newEntry() }, kind = TeddButtonKind.Secondary, icon = "plus", modifier = Modifier.weight(1f))
                    }
                    if (entries.size > 3) {
                        org.teddapp.project.ui.TeddSearchField(model.search, { model.search = it }, "Chercher une entrée")
                    }
                    val q = model.search.trim()
                    val visible = if (q.isEmpty()) entries else entries.filter {
                        it.entry.title.contains(q, ignoreCase = true) || it.entry.username.contains(q, ignoreCase = true) ||
                            it.entry.url.contains(q, ignoreCase = true) || it.entry.content.contains(q, ignoreCase = true)
                    }
                    Text(
                        text = "${visible.size} entrée${if (visible.size > 1) "s" else ""}",
                        style = TeddType.MonoSm.copy(fontSize = 11.sp),
                        color = TeddTokens.InkMuted,
                        modifier = Modifier.padding(start = TeddSpace.S1, top = TeddSpace.S1),
                    )
                    visible.forEach { e ->
                        EntryCard(
                            e = e,
                            onOpen = { model.selectedItemId = e.row.id },
                            onCopy = {
                                val block = e.entry.content.isNotEmpty()
                                clipboard.setText(AnnotatedString(if (block) e.entry.content else e.entry.secret))
                                model.snackbar.show(if (block) "Contenu copié" else "Secret copié")
                            },
                        )
                    }
                    if (entries.isEmpty()) {
                        TeddEmptyState(
                            title = "Un trousseau vide",
                            message = "Colle une requête, un JSON, une commande, ou note un identifiant. Tout est chiffré avant d'être écrit.",
                            icon = "lock",
                        )
                    } else if (visible.isEmpty()) {
                        Text("Aucune entrée ne correspond.", color = TeddTokens.InkMuted, style = TeddType.Body, modifier = Modifier.padding(TeddSpace.S2))
                    }
                }
            }
        }
        TeddBottomNav(active = Section.Vault, onNavigate = onNavigate, modifier = Modifier.navigationBarsPadding())
    }
}

@Composable
private fun VaultTitle(onLock: (() -> Unit)?) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Text("Trousseau", style = TeddType.Title1, color = TeddTokens.Ink, modifier = Modifier.weight(1f))
        if (onLock != null) TeddButton(text = "Verrouiller", onClick = onLock, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "lock")
    }
}

/** Les trousseaux en pastilles : celui qu'on regarde, les autres, et de quoi en créer un. */
@Composable
private fun KeychainChips(model: VaultModel) {
    val current = model.selectedKeychain?.id
    Row(modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()), horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        model.keychains.forEach { kc ->
            org.teddapp.project.ui.TeddFilterChip(
                label = kc.name,
                selected = kc.id == current,
                onClick = { model.select(kc.id) },
                icon = if (model.isUnlocked(kc.id)) "check" else "lock",
            )
        }
        org.teddapp.project.ui.TeddFilterChip(label = "Trousseau", selected = false, onClick = { model.creating = true; model.error = null }, icon = "plus")
    }
}

/**
 * Carte d'une entrée sur téléphone. « Copier » est sur la carte : on revient au trousseau
 * pour recopier quelque chose, pas pour l'ouvrir. Le secret lui-même ne s'affiche pas.
 */
@Composable
private fun EntryCard(e: OpenEntry, onOpen: () -> Unit, onCopy: () -> Unit) {
    val block = e.entry.content.isNotEmpty()
    org.teddapp.project.ui.TeddListCard(onClick = onOpen) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            Box(
                modifier = Modifier.size(40.dp).background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm)),
                contentAlignment = Alignment.Center,
            ) {
                if (block) Text(VaultFormat.of(e.entry.format).label.take(4), color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 10.sp), maxLines = 1)
                else TeddIcon(name = "lock", size = 18.dp, color = TeddTokens.InkMuted)
            }
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(e.entry.title.ifBlank { "Sans titre" }, color = TeddTokens.Ink, style = TeddType.BodyStrong, maxLines = 1, overflow = TextOverflow.Ellipsis)
                val sub = if (block) e.entry.content.lineSequence().firstOrNull { it.isNotBlank() }?.trim().orEmpty() else e.entry.username.ifBlank { e.entry.url }
                if (sub.isNotBlank()) {
                    Text(sub, color = TeddTokens.InkMuted, style = if (block) TeddType.MonoSm.copy(fontSize = 12.sp) else TeddType.Caption, maxLines = 1, overflow = TextOverflow.Ellipsis)
                }
            }
            if (block || e.entry.secret.isNotEmpty()) {
                TeddButton(text = "Copier", onClick = onCopy, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm)
            }
        }
    }
}
